import {
  Component,
  computed,
  inject,
  input,
  model,
  OnDestroy,
  Signal,
  signal,
} from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ICONS } from '@constants/icons';
import { COLORS } from '@constants/colors';
import { URLS } from '@constants/urls';
import { Router, RouterModule } from '@angular/router';
import { AbbCommonModule } from 'app/abb-common.module';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import {
  Event_Category,
  MarkAllAsReadMutation,
  NewNotificationSubscription,
  Notification,
  NotificationListQuery,
  RegisterLogoutMutation,
  UiSettings,
  UserAccount,
} from 'gql/graphql';
import { CdkMenuTrigger, CdkMenu, CdkMenuItem } from '@angular/cdk/menu';
import { Apollo } from 'apollo-angular';
import { Subscription } from 'rxjs';
import {
  markAllAsReadMutation,
  newNotificationSubscription,
  notificationListQuery,
} from '@graphql/notifications';
import { CommonModule } from '@angular/common';
import { EVENT_CATEGORY } from '@constants/user';
import { AbbDateComponent } from '@components/abb-date/abb-date.component';
import { NotificationsService } from '@services/ui/notifications.service';
import { CognitoService, UserWithID } from '@services/cognito/cognito.service';
import { getErrorMessage } from '@utils/errors';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/ui/toast.service';
import { UserSettingsService } from '@services/user/user-settings.service';
import { CompanySelectComponent } from '../components/abb-company-select/abb-company-select.component';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { registerLogout } from '@graphql/user';
import { S } from '@angular/cdk/keycodes';

@Component({
  selector: 'abb-header',
  standalone: true,
  imports: [
    CommonModule,
    CdkMenuTrigger,
    CdkMenu,
    CdkMenuItem,
    MatToolbarModule,
    AbbCommonModule,
    RouterModule,
    TPipe,
    AbbDateComponent,
    CompanySelectComponent,
    OverflowTooltipDirective,
  ],
  template: `
    <mat-toolbar
      class="border-b-2 border-b-solid border-b-abb-black/20 relative z-auto"
    >
      <div class="flex items-center h-full">
        <a [routerLink]="URLS.BASE" class="h-full">
          @if(!uiSettings()?.logo?.custom) {
          <abb-logo
            class="block h-full content-center"
            svgClassName="w-full h-2/6 ml-1"
          />
          } @else {
          <div
            class="block bg-cover h-full aspect-square mx-2"
            [ngStyle]="{
              backgroundImage: 'url(' + uiSettings()?.logo?.custom + ')'
            }"
          ></div>
          }
        </a>
        <div class="flex items-center ml-6 mr-1">
          <div class="h-[1.75rem] w-[2px] bg-gray-300"></div>
        </div>
        <span
          class="flex ml-1 h-full items-center text-abb-font text-lg font-semibold min-h-9"
          >InSite Energy Pro</span
        >
      </div>
      <span class="flex-auto"></span>
      @if(!isWizardHeader()) {
      <abb-button
        variant="none"
        [cdkMenuTriggerFor]="notificationsMenu"
        (cdkMenuOpened)="notifyBellBadgeRemoved.set(true)"
        (cdkMenuClosed)="markAllAsRead()"
        [badge]="notifyBellBadge()"
        [icon]="'mat/notifications_none'"
        [iconSize]="'medium'"
        class="text-abb-font/60"
      />
      <ng-template #notificationsMenu>
        <div
          cdkMenu
          class="bg-abb-background min-w-[280px] max-w-[378px] rounded-sm shadow-lg shadow-abb-black/[0.43] py-4 pl-4 pr-2"
        >
          <div
            class="flex flex-col gap-3 max-h-80 overflow-auto pr-2 pl-1 py-2"
          >
            @if (notifications().length === 0) {
            <div class="flex items-center justify-center text-sm text-gray-500">
              {{ 'No notifications to display' | t }}
            </div>
            } @else { @for (notification of notifications(); track
            notification?.id) {
            <div
              class="flex flex-row gap-2 items-center justify-center min-w-[300px] w-full"
            >
              <abb-icon
                [size]="'medium'"
                [name]="getStatusIcon(notification?.category)"
                class="cursor-default text-abb-error shrink-0"
                [ngClass]="getStatusColorClass(notification?.category)"
              />
              <div
                class="flex flex-col gap-1.5 rounded-md shadow-md shadow-abb-abb-black/[0.66] p-3 flex-1"
                [ngClass]="[
                  notification?.unread
                    ? 'bg-abb-primary-light/10 border-l-4 border-l-abb-primary/70'
                    : 'bg-abb-black/[0.02]'
                ]"
              >
                <span class="text-xs font-semibold">{{
                  EVENT_CATEGORY[notification?.category!]
                }}</span>
                <span class="text-xs">{{ notification?.description }}</span>
                <span class="text-xs">
                  <abb-date
                    [date]="notification?.dateTime ?? null"
                    [showTime]="true"
                    [hour12]="false"
                    [showYear]="false"
                    [useLocale]="'en-GB'"
                  ></abb-date>
                </span>
              </div>
            </div>
            } }
          </div>
        </div>
      </ng-template>
      <abb-avatar
        [cdkMenuTriggerFor]="menu"
        [userFullName]="userFullName()"
        [userImgUrl]="user()?.imgUrl"
        [size]="'xsmall'"
        class="mx-2 mb-2"
      ></abb-avatar>
      <ng-template #menu>
        <div
          cdkMenu
          class="bg-abb-background min-w-[280px] max-w-[378px] overflow-hidden rounded-sm shadow-lg shadow-abb-black/[0.43] px-6 py-4"
        > 
          <div class="w-full flex flex-col gap-4">
            <div class="w-full flex flex-col gap-1">
              <div class="flex flex-row items-center justify-between">
                <span class="flex items-center p-1 font-bold text-abb-primary">{{ "My Sites' Accounts" | t }}</span>
                <abb-button
                  cdkMenuItem
                  [variant]="'none'"
                  class="hover:underline focus:outline-none"
                  (click)="logout()"
                  className="p-1 text-base font-medium text-left !justify-start"
                  >{{ 'Logout' | t }}</abb-button
                >
              </div>
              <app-company-select [licenseId]="user()?.licenseId" />
            </div>
            <div class="w-full flex flex-row justify-between gap-4">
              <div class="flex items-center justify-center">
                <abb-avatar
                  [userFullName]="userFullName()"
                  [userImgUrl]="user()?.imgUrl"
                ></abb-avatar>
              </div>
              <div class="flex-1 overflow-hidden">
                <div class="w-full flex flex-col gap-2 text-abb-font">
                  <span
                    class="text-2xl font-semibold truncate max-w-full"
                    overflowTooltip
                    >{{ userFullName() }}</span
                  >
                  <span
                    class="text-sm font-normal truncate max-w-full"
                    overflowTooltip
                    >{{ userAccount()?.email }}</span
                  >
                  <abb-button
                    cdkMenuItem
                    [variant]="'none'"
                    class="mt-2 hover:underline"
                    (click)="goToMyAccount()"
                    className="p-1 text-base font-medium text-left w-full !justify-start"
                    >{{ 'My Account' | t }}</abb-button
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </ng-template>
      }
    </mat-toolbar>
  `,
  styles: ``,
  providers: [{ provide: TRANSLATION_PREFIX, useValue: 'commons' }],
})
export class HeaderComponent implements OnDestroy {
  prefix = inject(TRANSLATION_PREFIX);
  title = input<string | undefined>();
  readonly ICONS = ICONS;
  readonly COLORS = COLORS;
  readonly URLS = URLS;
  readonly EVENT_CATEGORY = EVENT_CATEGORY;
  uiSettings: Signal<UiSettings | null>;
  user: Signal<UserWithID | null>;
  userAccount: Signal<UserAccount | null>;
  userFullName = computed(() =>
    [this.userAccount()?.name, this.userAccount()?.lastName].join(' ')
  );
  userBadgeLabel = computed(() =>
    this.userFullName()
      ?.split(' ')
      ?.map((w: string) => w[0]?.toUpperCase())
      .join('')
  );
  isWizardHeader = input(false);
  notifyBellBadgeRemoved = signal(false);
  inAppPushNotificationsEnabled = signal(false);
  notifyBellBadge = computed(
    () =>
      !this.notifyBellBadgeRemoved() &&
      this.inAppPushNotificationsEnabled() &&
      this.notifications().some((n) => n?.unread)
  );
  notifications = model<Array<Notification | null>>([]);
  loading = signal(true);

  private subscriptions: Subscription[] = [];
  constructor(
    public readonly apollo: Apollo,
    private readonly cognitoService: CognitoService,
    public readonly router: Router,
    private readonly notificationsService: NotificationsService,
    private readonly translate: TranslateService,
    private readonly toast: ToastService,
    private readonly userService: UserSettingsService
  ) {
    this.user = this.cognitoService.loggedUser;
    this.uiSettings = this.userService.uiSettings;
    this.userAccount = this.userService.userAccount;
    this.subscriptions.push(
      apollo
        .watchQuery<NotificationListQuery>({
          query: notificationListQuery,
          pollInterval: 60000,
          errorPolicy: 'all',
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.notifications.set(data.notificationList);
            this.notifyBellBadgeRemoved.set(false);
            this.loading.set(false);
          },
          error: (err) => {
            this.loading.set(false);
          },
        }),
      notificationsService.inAppPushEnabled$.asObservable().subscribe((v) => {
        this.inAppPushNotificationsEnabled.set(
          Object.values(v).every((val) => val === true)
        );
        //TODO: per il momento le disabilito se almeno uno è disabilitato, da sistemare inserendo le casistiche per evento (da specificare in qualche modo)
        if (this.inAppPushNotificationsEnabled()) {
          this.subscriptions.push(
            this.apollo
              .subscribe<NewNotificationSubscription>({
                query: newNotificationSubscription,
              })
              .subscribe({
                next: ({ data }) => {
                  if (data?.newNotification) {
                    // Add the new notification to the list
                    const updatedNotificationList = [
                      ...data?.newNotification,
                      ...this.notifications(),
                    ];
                    // Write the updated list back to the cache
                    apollo.client.writeQuery({
                      query: notificationListQuery,
                      data: { notificationList: updatedNotificationList },
                    });
                    this.notifyBellBadgeRemoved.set(false);
                  }
                },
                error: (err) => {
                  //let errMsg = getErrorMessage(err, this.translate);
                  //this.toast.notifyError(errMsg);
                },
              })
          );
        }
      })
    );
  }

  markAllAsRead() {
    this.subscriptions.push(
      this.apollo
        .mutate<MarkAllAsReadMutation>({
          mutation: markAllAsReadMutation,
          variables: { ids: this.notifications().map((n) => n?.id) },
        })
        .subscribe({
          next: ({ data }) => {
            this.notifications.set(data?.markAllAsRead ?? []);
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
  }

  getStatusIcon(status: Event_Category | undefined) {
    if (status === Event_Category.Critical) {
      return 'mat/error_outline';
    } else if (status === Event_Category.Information) {
      return 'mat-outlined/warning_amber';
    } else {
      return 'mat-outlined/settings';
    }
  }

  getStatusColorClass(status: Event_Category | undefined): string {
    if (status === Event_Category.Critical) {
      return '';
    } else if (status === Event_Category.Information) {
      return 'text-abb-warning';
    } else {
      return 'text-abb-gray-03';
    }
  }

  goToMyAccount() {
    this.router.navigate([`${URLS.BASE}${URLS.SETTINGS}/${URLS.ACCOUNT}`]);
  }

  logout() {
    this.apollo
      .mutate<RegisterLogoutMutation>({
        mutation: registerLogout,
      })
      .subscribe({
        next: () => {
          this.cognitoService
            .signOut()
            .catch((err) => {
              console.error('Sign out error:', err);
              let errMsg = getErrorMessage(err, this.translate);
              this.toast.notifyError(errMsg);
            })
            .then(() => {
              this.userService.setUserAccount(null);
              this.userService.cleanUiSettings();
            });
        },
        error: (err) => {},
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
